import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserProfile, updateUserProfile, convertDbProfileToCamelCase } from '../../../services/profileService';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const JobSeekerSettings = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    experienceLevel: '',
    expectedSalary: '',
    preferredLocations: [],
    remotePreference: '',
    skills: [],
    resumeFiles: []
  });

  const experienceLevelOptions = [
    { value: 'fresher', label: 'Fresher (0-1 years)' },
    { value: 'intermediate', label: 'Intermediate (1-3 years)' },
    { value: 'experienced', label: 'Experienced (3-5 years)' },
    { value: 'senior', label: 'Senior (5+ years)' }
  ];

  const locationOptions = [
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi', label: 'Delhi NCR' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'pune', label: 'Pune' },
    { value: 'chennai', label: 'Chennai' }
  ];

  const remoteOptions = [
    { value: 'onsite', label: 'On-site Only' },
    { value: 'remote', label: 'Remote Only' },
    { value: 'hybrid', label: 'Hybrid' }
  ];

  const skillOptions = [
    { value: 'react', label: 'React' },
    { value: 'nodejs', label: 'Node.js' },
    { value: 'mongodb', label: 'MongoDB' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'aws', label: 'AWS' }
  ];

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  const loadUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const profile = await getUserProfile(user?.id);
      const camelCaseProfile = convertDbProfileToCamelCase(profile);
      
      setFormData({
        experienceLevel: camelCaseProfile?.experienceLevel || '',
        expectedSalary: camelCaseProfile?.expectedSalary || '',
        preferredLocations: camelCaseProfile?.preferredLocations || [],
        remotePreference: camelCaseProfile?.remotePreference || '',
        skills: camelCaseProfile?.skills || [],
        resumeFiles: camelCaseProfile?.resumeFiles || []
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;
    
    try {
      setSaving(true);
      await updateUserProfile(user?.id, formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving job preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadUserProfile();
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading job preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Briefcase" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Job Preferences</h2>
            <p className="text-sm text-muted-foreground">Configure your job search preferences and resume</p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
            disabled={loading}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Resume Management</h3>
          <div className="space-y-3">
            {formData?.resumeFiles?.length > 0 ? (
              formData?.resumeFiles?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">{file?.name}</p>
                        {file?.isPrimary && (
                          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                            Primary
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {file?.size} • Uploaded on {file?.uploadDate}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="p-2 hover:bg-background rounded-lg transition-smooth">
                      <Icon name="Download" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-2 hover:bg-background rounded-lg transition-smooth">
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 bg-muted/50 rounded-lg text-center">
                <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No resume files uploaded yet</p>
                <Button variant="outline" size="sm" iconName="Upload" iconPosition="left">
                  Upload Resume
                </Button>
              </div>
            )}
            {formData?.resumeFiles?.length > 0 && (
              <Button variant="outline" size="sm" iconName="Upload" iconPosition="left" className="w-full sm:w-auto">
                Upload New Resume
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Experience Level"
            options={experienceLevelOptions}
            value={formData?.experienceLevel}
            onChange={(value) => setFormData(prev => ({ ...prev, experienceLevel: value }))}
            disabled={!isEditing}
          />

          <Input
            label="Expected Salary (₹ per annum)"
            type="text"
            value={formData?.expectedSalary}
            onChange={(e) => setFormData(prev => ({ ...prev, expectedSalary: e?.target?.value }))}
            disabled={!isEditing}
            placeholder="e.g., 8,00,000"
          />

          <Select
            label="Preferred Locations"
            options={locationOptions}
            value={formData?.preferredLocations}
            onChange={(value) => setFormData(prev => ({ ...prev, preferredLocations: value }))}
            disabled={!isEditing}
            multiple
            searchable
            description="Select multiple locations"
          />

          <Select
            label="Remote Work Preference"
            options={remoteOptions}
            value={formData?.remotePreference}
            onChange={(value) => setFormData(prev => ({ ...prev, remotePreference: value }))}
            disabled={!isEditing}
          />
        </div>

        <Select
          label="Skills"
          options={skillOptions}
          value={formData?.skills}
          onChange={(value) => setFormData(prev => ({ ...prev, skills: value }))}
          disabled={!isEditing}
          multiple
          searchable
          clearable
          description="Add your technical and professional skills"
        />

        {isEditing && (
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobSeekerSettings;